import { useState } from "react";
import { Button, Input, Stack, useToast } from "@salesapp/ui";
import { crmApi } from "../../lib/crmApi";
import { reportSaveBlocked, reportSaveFailure } from "../../lib/saveNotice";
import { FIELD_HINTS } from "../../help/fieldHints";

type Props = {
  orgId: string;
  companyId?: string | null;
  leadId?: string | null;
  opportunityId?: string | null;
  defaultSubject: string;
  onSaved: () => Promise<void> | void;
};

export function ActivityCommentForm({ orgId, companyId, leadId, opportunityId, defaultSubject, onSaved }: Props) {
  const { show } = useToast();
  const [subject, setSubject] = useState("");
  const [subjectFocused, setSubjectFocused] = useState(false);
  const [body, setBody] = useState("");
  const [outcome, setOutcome] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!body.trim() && !outcome.trim()) {
      setError(reportSaveBlocked(show, "Add details / comments before logging the activity."));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await crmApi.createActivity(
        {
          activity_type: "note",
          subject: subject.trim() || defaultSubject,
          body: body.trim() || null,
          outcome: outcome.trim() || null,
          company_id: companyId || null,
          lead_id: leadId || null,
          opportunity_id: opportunityId || null,
          occurred_at: new Date().toISOString(),
        },
        orgId,
      );
      setSubject("");
      setSubjectFocused(false);
      setBody("");
      setOutcome("");
      show("Activity logged", "success");
      await onSaved();
    } catch (err) {
      setError(reportSaveFailure(show, err, "Failed to log activity"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="crm-panel-card">
      <Stack gap={3}>
        <h2 className="crm-section-title">Log comment</h2>
        {error ? <p className="crm-error">{error}</p> : null}
        <Input
          label="Subject"
          tooltip={FIELD_HINTS.activity_subject}
          value={subject}
          placeholder={subjectFocused ? "" : defaultSubject}
          onChange={(event) => setSubject(event.target.value)}
          onFocus={() => setSubjectFocused(true)}
          onBlur={() => setSubjectFocused(false)}
        />
        <label className="crm-field">
          <span>Details / comments</span>
          <textarea
            className="crm-textarea crm-textarea--activity"
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={6}
          />
        </label>
        <Input
          label="Outcomes"
          tooltip={FIELD_HINTS.activity_outcome}
          value={outcome}
          onChange={(event) => setOutcome(event.target.value)}
        />
        <div className="crm-form-actions">
          <Button onClick={() => save()} disabled={saving}>
            {saving ? "Logging..." : "Log activity"}
          </Button>
        </div>
      </Stack>
    </div>
  );
}
